// Focus management for a modal surface (Dialog, SlideOver): while active, Tab and Shift+Tab cycle inside the
// container, Escape calls onEscape (unless it comes from an open menu, listbox or expanded combobox, which closes
// first; the next Escape reaches the trap), focus moves in on activation (the initial element, else the first focusable, else
// the container) and returns to the opener on deactivation. A no-op in `preview` (a gallery section's static,
// inert rendering of an open dialog).
// The trap follows the container ELEMENT, not only the ref: when the element behind the ref changes while active (an
// overlay drawn in place on mount and moved into the portal layer on the next render), the trap re-attaches to the
// new element and moves focus into it. The opener is captured once per activation, so a re-attachment never returns
// focus to the opener in between.

import { useEffect, useRef, useState, type RefObject } from "react";
import { useIsoLayoutEffect } from "./useIsoLayoutEffect";

export interface FocusTrapOptions {
  active: boolean;
  onEscape?: (() => void) | undefined;
  preview?: boolean | undefined;
  initialFocus?: RefObject<HTMLElement | null> | undefined;
  /** Default true. */
  returnFocus?: boolean | undefined;
}

const FOCUSABLE = [
  "a[href]", "area[href]", "button:not([disabled])", "input:not([disabled]):not([type=hidden])", "select:not([disabled])",
  "textarea:not([disabled])", "iframe", "[contenteditable=true]", "[tabindex]:not([tabindex='-1'])",
].join(",");

export function focusables(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE))
    .filter((element) => !element.hasAttribute("hidden") && element.closest("[hidden],[inert]") === null && element.tabIndex >= 0);
}

export function useFocusTrap(container: RefObject<HTMLElement | null>, options: FocusTrapOptions): void {
  const { active, preview = false, returnFocus = true } = options;
  const latest = useRef(options);
  latest.current = options;
  const on = active && !preview;

  // The element behind the ref, read after every commit; a change re-runs the trap below.
  const [node, setNode] = useState<HTMLElement | null>(null);
  useIsoLayoutEffect(() => {
    const current = on ? container.current : null;
    if (current !== node) setNode(current);
  });

  // The opener: taken when the trap turns on (before focus moves in), focused again when it turns off.
  useEffect(() => {
    if (!on) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      if (returnFocus && opener && opener.isConnected) opener.focus();
    };
  }, [on, returnFocus]);

  useEffect(() => {
    const root = node;
    if (!on || !root) return;
    const first = latest.current.initialFocus?.current ?? focusables(root)[0] ?? root;
    if (first === root && root.tabIndex < 0) root.tabIndex = -1;
    first.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && latest.current.onEscape) {
        // An inner menu (portalled into the layer or not), listbox or open combobox takes this Escape itself.
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest("[role=menu],[role=listbox],[role=combobox][aria-expanded=true]")) return;
        event.preventDefault();
        event.stopPropagation();
        latest.current.onEscape();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusables(root);
      if (items.length === 0) {
        event.preventDefault();
        root.focus();
        return;
      }
      const head = items[0] as HTMLElement;
      const tail = items[items.length - 1] as HTMLElement;
      const at = document.activeElement;
      if (event.shiftKey && (at === head || !root.contains(at))) {
        event.preventDefault();
        tail.focus();
      } else if (!event.shiftKey && (at === tail || !root.contains(at))) {
        event.preventDefault();
        head.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [on, node]);
}
